import { Component, inject } from '@angular/core';
import { filter, Observable } from 'rxjs';
import { HeadlineButtonContext } from '@ui/layout/layout.model';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';
import { HeadlineButtonComponent } from '@ui/layout/headline-button/headline-button.component';
import { Router } from '@angular/router';
import { DialogService } from '@ui/dialog-v2/dialog.service';
import { RouteEntity } from '@core/router/route.entity';
import { B2bProgramsState } from '../../shared/state/b2b-programs-state';
import { StartWithUnit } from '@core/state/operators/start-with-unit';
import { RxEffects } from '@rx-angular/state/effects';
import { HeadlineWrapperModule } from '@ui/layout/headline-wrapper/headline-wrapper.module';
import { HeadlineModule } from '@ui/layout/headline/headline.module';
import { TableB2bProgramsComponent } from './table-b2b-programs/table-b2b-programs.component';
import { PartnerProgram } from '@api/models/B2bService/Contract/partner-program';
import { ContentWrapperModule } from '@ui/layout/content-wrapper/content-wrapper.module';
import { AsyncPipe } from '@angular/common';
import { BtnLabels } from '@ui/models/btn.labels';
import { PrivilegesModules } from '@core/privileges/privileges-modules';
import { HasPrivilege } from '@core/privileges/has-privilege';
import { SelectProgramTypeDialogComponent } from './select-program-type-dialog/select-program-type-dialog.component';
import { B2bProgramType } from '../../shared/models/b2b-program-type';
import { RoutingQueryParamsName } from '@core/router/routing.params';
import { getLoadingProp } from '@core/state/operators/start-with-loading';
import { marker } from '@colsen1991/ngx-translate-extract-marker';


@Component({
    selector: 'b2b-page-programs',
    templateUrl: './programs.component.html',
    styleUrls: ['./programs.component.scss'],
    providers: [RxEffects],
    imports: [HeadlineWrapperModule, HeadlineModule, TableB2bProgramsComponent, ContentWrapperModule, AsyncPipe, TranslatePipe],
})
export class ProgramsComponent {
    @HasPrivilege([PrivilegesModules.B2bLoad])
    public readonly isEditUser$: Observable<boolean>;

    readonly #b2bProgramsState: B2bProgramsState = inject(B2bProgramsState);
    readonly #translate: TranslateService = inject(TranslateService);
    readonly #router: Router = inject(Router);
    readonly #dialog: DialogService = inject(DialogService);
    readonly #effects: RxEffects = inject(RxEffects);

    public readonly b2bPrograms$: Observable<PartnerProgram[]> = this.#b2bProgramsState.programs$;
    public readonly pending$: Observable<boolean> = this.#b2bProgramsState.loading$.pipe(
        getLoadingProp(StartWithUnit.RequestB2BPrograms),
    );

    public readonly pendingDelete$: Observable<boolean> = this.#b2bProgramsState.loading$.pipe(
        getLoadingProp(StartWithUnit.RequestRemoveB2BProgram),
    );

    public readonly content: PolymorpheusComponent<HeadlineButtonComponent> = new PolymorpheusComponent(HeadlineButtonComponent);
    public headlineButtonContext: HeadlineButtonContext;

    constructor() {
        this.#effects.register(
            this.#translate.get(BtnLabels.Add),
            (value: string) =>
                (this.headlineButtonContext = {
                    text: value,
                    onClick: this.handlerAddProgram.bind(this),
                }),
        );
    }

    public handlerAddProgram(): void {
        this.#effects.register(
            this.#dialog
                .openDialog(SelectProgramTypeDialogComponent, {
                    title: marker('b2b.dialog.select-type-b2bProgram.head'),
                })
                .afterClosed()
                .pipe(filter((value: B2bProgramType) => [B2bProgramType.SingleProduct, B2bProgramType.NumberOfTrips].includes(value))),
            (b2bProgramType: B2bProgramType) => {
                this.#router.navigate([RouteEntity.Root, RouteEntity.Programs, RouteEntity.Add], {
                    queryParams: {
                        [RoutingQueryParamsName.B2bProgramType]: b2bProgramType,
                    },
                });
            },
        );
    }

    public handlerDeleteProgram(program: PartnerProgram): void {
        this.#dialog.openConfirmPopup(
            this.#b2bProgramsState.removeProgram.bind(this.#b2bProgramsState, program.prgId),
            this.pendingDelete$,
        );
    }

    public handlerClickRow(id: number): void {
        this.#router.navigate([RouteEntity.Root, RouteEntity.Programs, String(id)]);
    }
}
